import React, { ReactElement } from 'react';

import { Box, CircularProgress, Container, Typography } from '@material-ui/core';
import FavoriteIcon from '@material-ui/icons/Favorite';
import { useAuth, useSimpleSearch } from 'hooks';
import useMounted from 'hooks/mounted';
import { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { useSearchFavorite } from 'queries';
import { useFavorites } from 'queries/favorites/useFavorites';
import { FormattedMessage, useIntl } from 'react-intl';

import Background from 'components/@common/Background';
import LoadingContentCard from 'components/@common/ContentCard/Loading/LoadingContentCard';
import Icon from 'components/@common/Icon';
import SearchEmptyState from 'components/@common/Search/SearchEmptyState';
import SearchInput from 'components/@common/Search/SearchInput';
import Empty from 'components/@favorites/Empty';
import GameSection from 'components/@game-detail/Section';
import NotLoggedInCta from 'components/@home/NotLoggedInCta';
import NavigationLayout from 'components/@layout/NavigationLayout/NavigationLayout';
import HeaderSimple from 'components/@navigation/HeaderSimple';

const Discover: NextPage = () => {
  const { isLoggedIn } = useAuth();
  const intl = useIntl();
  const { games, isLoading: favoritesIsLoading, isEmpty } = useFavorites();
  const { currentValue, debouncedValue, searchChange, searchClear } = useSimpleSearch();
  const { searchData, isLoading: searchIsLoading } = useSearchFavorite(debouncedValue);

  const isMounted = useMounted();
  const isLoading = favoritesIsLoading || !isMounted;
  const render = () => {
    if (isLoading) {
      return (
        <Box mt={6}>
          <Typography variant="h4" component="h1" color="textPrimary">
            <FormattedMessage id="favorites.title" />
          </Typography>
          <Box mt={4}>
            {[...new Array(8)].map((_, index) => (
              <Box key={index} mt={2}>
                <LoadingContentCard type="sm" />
              </Box>
            ))}
          </Box>
        </Box>
      );
    } else if (!isLoggedIn) {
      return (
        <Box height="100%" alignItems="center" display="flex">
          <NotLoggedInCta
            textAlign="center"
            icon={<Icon size={56} Icon={FavoriteIcon} />}
            title={intl.formatMessage({ id: 'favorites.login.title' })}
            description={intl.formatMessage({ id: 'favorites.login.description' })}
            titleVariant="h5"
          />
        </Box>
      );
    } else if (isEmpty) {
      return <Empty />;
    } else {
      return (
        <Box>
          <HeaderSimple
            noBack
            rightItems={
              <SearchInput
                value={currentValue}
                onChange={searchChange}
                onClear={searchClear}
                fullWidth
              />
            }
          />
          <Box mt={6}>
            <Box mb={4}>
              <Typography variant="h4" component="h1" color="textPrimary">
                <FormattedMessage id="favorites.title" />
              </Typography>
            </Box>
            {!!debouncedValue && searchIsLoading && (
              <Box mt={16} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            )}
            {!!debouncedValue && !searchIsLoading && !searchData.length && (
              <Box mt={16}>
                <SearchEmptyState value={currentValue} onClear={searchClear} />
              </Box>
            )}
            {games && (
              <GameSection
                id="favorites"
                data={debouncedValue ? searchData : games}
                variant="detail"
                imagesAboveFold
                disablePadding
              />
            )}
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      <NextSeo
        title={intl.formatMessage({ id: 'seo.title.favorites' })}
        //description={intl.formatMessage({ id: 'seo.description.favorites' })}
      />
      <Background />
      <Box component="main" height="100%" mb={6}>
        <Container maxWidth="sm" style={{ height: '100%' }}>
          {render()}
        </Container>
      </Box>
    </>
  );
};

// @ts-ignore
Discover.getLayout = function getLayout(page: ReactElement) {
  return <NavigationLayout headerVariant="none">{page}</NavigationLayout>;
};

export default Discover;
